import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { API, stApi, iceServers, errorMessage } from './api'
import { PollingSignal, waitForIceGathering } from './signaling'
import './stDiagnostic.css'

const REFRESH_MS = 15000

function StreamVideo({ stream, muted = true, className, onClick }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream || null
  }, [stream])
  useEffect(() => {
    if (ref.current) ref.current.muted = muted
  }, [muted])
  return <video ref={ref} className={className} autoPlay playsInline muted={muted} onClick={onClick} />
}

const timeLeft = (deadline) => {
  const ms = new Date(deadline).getTime() - Date.now()
  if (ms <= 0) return 'time up'
  const m = Math.floor(ms / 60000)
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m left`
}

// Live view for signed-in admins: every candidate's shared screen, camera and mic.
// Nothing is recorded; the video flows directly from each candidate's browser to this one.
export default function StProctorView() {
  const [ticket, setTicket] = useState(null)
  const [error, setError] = useState('')
  const [needsLogin, setNeedsLogin] = useState(false)
  const [attempts, setAttempts] = useState([])
  const [live, setLive] = useState({}) // attemptId -> { peerId, name, email, camStream, screenStream, state }
  const [signalingUp, setSignalingUp] = useState(false)
  const [unmuted, setUnmuted] = useState(null)
  const [focused, setFocused] = useState(null)
  const [, setClock] = useState(0)

  const peersRef = useRef(new Map()) // peerId -> { pc, attemptId, streamKinds }
  // Mirror of the live candidate list, readable inside signal handlers without re-subscribing.
  const liveRef = useRef({})

  // The admin's sign-in is what grants access; exchange it for a live-view ticket.
  useEffect(() => {
    stApi.post('/admin/live-ticket')
      .then(({ data }) => setTicket(data.ticket))
      .catch(err => {
        if (err?.response?.status === 401) setNeedsLogin(true)
        else setError(err?.response?.status === 403 ? 'Only admins can watch the diagnostic live.' : errorMessage(err))
      })
  }, [])

  // Candidates currently taking the diagnostic, refreshed periodically.
  useEffect(() => {
    if (!ticket) return
    const load = () => axios.get(`${API}/api/st-diagnostic/proctor/attempts`, { headers: { Authorization: `Bearer ${ticket}` } })
      .then(({ data }) => setAttempts(data.attempts))
      .catch(err => { if (err?.response?.status === 401) setError(errorMessage(err)) })
    load()
    const timer = setInterval(() => { load(); setClock(c => c + 1) }, REFRESH_MS)
    return () => clearInterval(timer)
  }, [ticket])

  // Live media over WebRTC; candidates send the offers.
  useEffect(() => {
    if (!ticket) return
    const peers = peersRef.current

    const patch = (attemptId, values) => setLive(prev => ({ ...prev, [attemptId]: { ...(prev[attemptId] || {}), ...values } }))

    const closePeer = (peerId) => {
      const peer = peers.get(peerId)
      if (!peer) return
      peer.pc.close()
      peers.delete(peerId)
    }

    const signal = new PollingSignal(ticket)

    const handleOffer = async (from, data) => {
      closePeer(from)
      const attemptId = Object.values(liveRef.current).find(c => c.peerId === from)?.attemptId
      if (!attemptId) return
      const pc = new RTCPeerConnection({ iceServers: iceServers() })
      const peer = { pc, attemptId, streamKinds: data.streams || {} }
      peers.set(from, peer)

      pc.ontrack = (e) => {
        const stream = e.streams[0]
        const kind = peer.streamKinds[stream?.id]
        if (kind === 'camera') patch(attemptId, { camStream: stream })
        if (kind === 'screen') patch(attemptId, { screenStream: stream })
      }
      pc.onconnectionstatechange = () => patch(attemptId, { state: pc.connectionState })

      await pc.setRemoteDescription(data.sdp)
      await pc.setLocalDescription(await pc.createAnswer())
      await waitForIceGathering(pc)
      if (peers.get(from)?.pc !== pc) return
      await signal.send(from, { type: 'answer', sdp: pc.localDescription })
    }

    signal
      .on('connect', () => setSignalingUp(true))
      .on('disconnect', () => setSignalingUp(false))
      .on('unauthorized', (message) => {
        setSignalingUp(false)
        setError(message || 'Your admin session has expired. Reload the page.')
      })
      .on('peer-joined', (c) => {
        // A reconnecting candidate gets a new peer id; drop the old connection.
        const previous = liveRef.current[c.attemptId]
        if (previous && previous.peerId !== c.peerId) closePeer(previous.peerId)
        liveRef.current = { ...liveRef.current, [c.attemptId]: { ...c, state: 'connecting' } }
        setLive(prev => ({ ...prev, [c.attemptId]: { ...c, state: 'connecting' } }))
      })
      .on('peer-left', ({ peerId, attemptId }) => {
        closePeer(peerId)
        if (liveRef.current[attemptId]?.peerId === peerId) {
          const { [attemptId]: _, ...rest } = liveRef.current
          liveRef.current = rest
          setLive(rest)
        }
      })
      .on('signal', ({ from, data }) => {
        if (data?.type === 'offer') handleOffer(from, data).catch(err => console.warn('Could not answer candidate', err))
      })
      .start()

    return () => {
      signal.stop()
      for (const id of [...peers.keys()]) closePeer(id)
      liveRef.current = {}
    }
  }, [ticket])

  if (needsLogin) {
    return (
      <div className="std"><div className="std-wrap"><div className="std-card">
        <h2>Please sign in</h2>
        <p>Sign in with an admin account to watch the diagnostic live.</p>
        <Link className="std-btn" to="/login">Sign in</Link>
      </div></div></div>
    )
  }
  if (error) {
    return <div className="std"><div className="std-wrap"><div className="std-card"><h2>Live view unavailable</h2><div className="std-alert std-alert-bad">{error}</div></div></div></div>
  }
  if (!ticket) return <div className="std"><div className="std-wrap">Opening live view…</div></div>

  // Merge in-progress attempts (API) with live connections (signaling).
  const cards = attempts.map(a => ({ attemptId: String(a._id), name: a.name, email: a.email, language: a.language, deadline: a.deadline, ...(live[String(a._id)] || {}), online: !!live[String(a._id)] }))
  for (const [attemptId, c] of Object.entries(live)) {
    if (!cards.some(x => x.attemptId === attemptId)) cards.push({ ...c, attemptId, online: true })
  }
  const focusedCard = cards.find(c => c.attemptId === focused)

  return (
    <div className="std">
      <div className="std-wrap-wide">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <div>
            <h1>Live view — Software Testing diagnostic</h1>
            <div className="std-muted std-small">{cards.length} candidate(s) in progress · click a screen to enlarge it · nothing is recorded</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className={`std-badge ${signalingUp ? 'std-badge-ok' : 'std-badge-bad'}`}>{signalingUp ? 'Connected' : 'Not connected'}</span>
            <Link className="std-btn std-btn-ghost std-btn-sm" to="/admin/software-testing">← Admin</Link>
          </div>
        </div>

        {cards.length === 0 && <div className="std-card std-muted">No one is taking the diagnostic right now. Candidates appear here as soon as they start.</div>}
        <div className="std-proctor-grid">
          {cards.map(c => (
            <div key={c.attemptId} className={`std-cand ${c.online ? '' : 'std-cand-offline'}`}>
              <div className="std-cand-head">
                <div>
                  <strong>{c.name}</strong>
                  <div className="std-muted std-small">{c.email}{c.language && ` · ${c.language}`}{c.deadline && ` · ${timeLeft(c.deadline)}`}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {!c.online && <span className="std-badge std-badge-bad">Not streaming</span>}
                  {c.online && c.state !== 'connected' && <span className="std-badge std-badge-warn">{c.state || 'connecting'}</span>}
                  <button className="std-btn std-btn-ghost std-btn-sm" disabled={!c.camStream}
                    onClick={() => setUnmuted(unmuted === c.attemptId ? null : c.attemptId)}>
                    {unmuted === c.attemptId ? '🔊 Mute' : '🔇 Listen'}
                  </button>
                </div>
              </div>
              <div className="std-cand-media">
                {c.screenStream
                  ? <StreamVideo stream={c.screenStream} onClick={() => setFocused(c.attemptId)} />
                  : <div className="std-cand-placeholder">{c.online ? 'Waiting for screen…' : 'Candidate is not connected'}</div>}
                {c.camStream && <StreamVideo stream={c.camStream} muted={unmuted !== c.attemptId} className="std-cand-cam" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {focusedCard && (
        <div className="std-focus">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', color: '#fff' }}>
            <strong>{focusedCard.name} — {focusedCard.email}</strong>
            <button className="std-btn std-btn-sm" onClick={() => setFocused(null)}>Close</button>
          </div>
          <StreamVideo stream={focusedCard.screenStream} />
        </div>
      )}
    </div>
  )
}
